import { Component, ErrorInfo, ReactNode } from 'react';
import { isStaleChunkError, reloadOnceForStaleChunk } from '@/utils/chunk-reload';

// project imports
import { reportError } from './error-bus';
import { ErrorFallback } from './error-fallback';
import { IErrorBoundaryProps, IErrorBoundaryState } from './types';

// ==============================|| ERROR BOUNDARY (fatal render crashes) ||============================== //
// Safety net for render errors anywhere in the tree. Shows the full-page
// fallback and also funnels the error to the global error dialog.

const HOME_PATH = '/common/project';

const goHome = (): void => {
	window.location.href = HOME_PATH;
};

export class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
	constructor(props: IErrorBoundaryProps) {
		super(props);
		this.state = { error: null, componentStack: null };
	}

	public static getDerivedStateFromError(error: Error): Partial<IErrorBoundaryState> {
		return { error };
	}

	public componentDidCatch(error: Error, info: ErrorInfo): void {
		// chunk vechi după un deploy (index.html-ul din tab referă hash-uri șterse) —
		// recuperarea corectă e reîncărcarea paginii, nu ecranul de eroare
		if (isStaleChunkError(error) && reloadOnceForStaleChunk()) return;

		const componentStack = info.componentStack ?? null;
		this.setState({ componentStack });

		// never swallow silently
		console.error('Uncaught error:', error, info);

		// funnel to the global error dialog too (exact message + stack incl. component stack)
		const augmented = new Error(error.message);
		augmented.name = error.name;
		augmented.stack = `${error.stack ?? ''}${
			componentStack ? `\n\nComponent stack:${componentStack}` : ''
		}`;
		reportError(augmented);
	}

	private handleReset = (): void => {
		this.setState({ error: null, componentStack: null });
	};

	public render(): ReactNode {
		const { error, componentStack } = this.state;
		const { children } = this.props;

		if (!error) return children;

		const stack = `${error.stack ?? ''}${
			componentStack ? `\n\nComponent stack:${componentStack}` : ''
		}`;

		return (
			<ErrorFallback
				name={error.name}
				message={error.message || String(error)}
				stack={stack}
				onRetry={this.handleReset}
				onHome={goHome}
			/>
		);
	}
}
